export type RequisitionStatusCategory = "Active" | "Draft" | "Hold" | "Closed";

export function getRequisitionStatus(state?: string): RequisitionStatusCategory {
  if (!state) return "Active";
  const s = state.toLowerCase().trim();
  if (s === "draft") return "Draft";
  if (s === "on_hold" || s === "hold" || s === "paused") return "Hold";
  if (s === "closed" || s === "finalized" || s === "archived") return "Closed";
  return "Active";
}

/* ── Compensation display — ONE formatter for the list, the dashboard and the requirement page
   (UX audit 14 / product review P0 #3: the list said "₹14L - ₹22L LPA" while the detail page said
   "₹15,00,000 - ₹25,00,000"). Every rupee range in the UI goes through formatBudgetRange. ── */

/** Parse a recruiter-typed rupee amount: 2400000, "₹24,00,000", "24L", "24 LPA", "2.4 Cr", "24 lakh".
 *  A bare number under 1000 is read as lakhs ("24" → 24L). Null when it can't be read. */
export function parseInrAmount(v: number | string | null | undefined): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === "number") return Number.isFinite(v) && v > 0 ? (v < 1000 ? v * 100000 : v) : null;
  const s = v.toLowerCase().replace(/[₹,\s]|rs\.?|inr/g, "");
  const m = s.match(/^(\d+(?:\.\d+)?)(cr|crore|l|lakh|lakhs|lac|lpa|k)?$/);
  if (!m) return null;
  const n = Number(m[1]);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = m[2];
  if (unit === "cr" || unit === "crore") return n * 10000000;
  if (unit === "k") return n * 1000;
  if (unit) return n * 100000;
  return n < 1000 ? n * 100000 : n;
}

/** ₹ amount in the short Indian form: ₹24L, ₹7.5L, ₹1.2Cr, ₹80K. */
export function formatInrShort(rupees: number): string {
  const trim = (n: number): string => (Math.round(n * 10) / 10).toString();
  if (rupees >= 10000000) return `₹${trim(rupees / 10000000)}Cr`;
  if (rupees >= 100000) return `₹${trim(rupees / 100000)}L`;
  if (rupees >= 1000) return `₹${Math.round(rupees / 1000)}K`;
  return `₹${Math.round(rupees)}`;
}

/** "₹24L – ₹32L" from two typed amounts; null when neither can be read. If only one side reads,
 *  the raw values are joined with the same separator so nothing typed is ever hidden. */
export function formatBudgetRange(
  min: number | string | null | undefined,
  max: number | string | null | undefined,
): string | null {
  const a = parseInrAmount(min);
  const b = parseInrAmount(max);
  if (a !== null && b !== null) return `${formatInrShort(a)} – ${formatInrShort(b)}`;
  if (a !== null && (max === undefined || max === null || max === "")) return `From ${formatInrShort(a)}`;
  if (b !== null && (min === undefined || min === null || min === "")) return `Up to ${formatInrShort(b)}`;
  if (min || max) return [min, max].filter((x) => x !== undefined && x !== null && x !== "").join(" – ");
  return null;
}

type BudgetReq = {
  title?: string;
  region?: string;
  config?: {
    budgetMin?: number | string;
    budgetMax?: number | string;
    salaryMin?: number;
    salaryMax?: number;
    salaryCurrency?: string;
  };
};

/** True when the budget shown comes from what the recruiter actually entered — false means
 *  getRequisitionBudget is showing a role-based estimate, which the UI must label as such. */
export function hasConfiguredBudget(req: BudgetReq): boolean {
  return Boolean((req.config?.budgetMin || req.config?.budgetMax) || (req.config?.salaryMin && req.config?.salaryMax));
}

export function getRequisitionBudget(req: BudgetReq): string {
  if (req.config?.budgetMin || req.config?.budgetMax) {
    const r = formatBudgetRange(req.config.budgetMin, req.config.budgetMax);
    if (r) return r;
  }
  if (req.config?.salaryMin && req.config?.salaryMax) {
    if (req.config.salaryCurrency === "INR" || req.region === "in-central") {
      return formatBudgetRange(req.config.salaryMin, req.config.salaryMax) ?? "";
    }
    return `$${req.config.salaryMin.toLocaleString()} – $${req.config.salaryMax.toLocaleString()}`;
  }
  const title = (req.title || "").toLowerCase();
  if (req.region === "in-central") {
    if (title.includes("nurse") || title.includes("icu")) return "₹6L – ₹10L";
    if (title.includes("support") || title.includes("customer")) return "₹5L – ₹8L";
    if (title.includes("hr") || title.includes("talent")) return "₹7L – ₹12L";
    if (title.includes("lead") || title.includes("senior")) return "₹24L – ₹32L";
    if (title.includes("frontend") || title.includes("backend") || title.includes("engineer")) return "₹18L – ₹26L";
    return "₹14L – ₹22L";
  }
  // Default USD / other regions
  if (title.includes("nurse") || title.includes("icu")) return "$75,000 – $95,000";
  if (title.includes("support") || title.includes("customer")) return "$55,000 – $75,000";
  if (title.includes("sales")) return "$85,000 – $120,000";
  if (title.includes("devops") || title.includes("cloud") || title.includes("infra")) return "$145,000 – $185,000";
  if (title.includes(".net") || title.includes("full stack") || title.includes("backend")) return "$130,000 – $165,000";
  return "$120,000 – $155,000";
}

export function getRequisitionDepartment(req: {
  title?: string;
  verticalKey?: string;
  config?: { department?: string; verticalKey?: string };
}): string {
  if (req.config?.department) return req.config.department;
  const title = (req.title || "").toLowerCase();
  const vert = (req.config?.verticalKey || req.verticalKey || "").toLowerCase();
  if (title.includes("nurse") || title.includes("icu") || vert === "healthcare") return "Clinical & Critical Care";
  if (title.includes("sales") || vert === "sales") return "Sales & Revenue";
  if (title.includes("support") || vert === "retail_ops") return "Customer Operations";
  if (title.includes("hr") || title.includes("talent")) return "People & Talent";
  if (title.includes("devops") || title.includes("cloud") || title.includes("sre") || title.includes("infra")) return "Cloud & Infrastructure";
  if (title.includes(".net") || title.includes("backend") || title.includes("java")) return "Core Services & Backend";
  if (title.includes("frontend") || title.includes("react")) return "Web & UI Platform";
  if (vert === "software_engineering") return "Engineering & Technology";
  return "Operations & Platform";
}

/** Interview depth levels → the years-of-experience band they target. Internal codes like "L1"
 *  must never reach the UI (product review: "Experience Target L1" leaked on the detail page). */
const LEVEL_YEARS: Record<string, [number, number | null]> = {
  L1: [1, 3],
  L2: [3, 5],
  L3: [5, null],
};

/** "L1" / "L1 + L2" / ["L2","L3"] → "1–3 yrs" / "1–5 yrs" / "3+ yrs". Null when not level codes. */
export function levelsToYears(levels: string | readonly string[] | null | undefined): string | null {
  const list = typeof levels === "string" ? levels.split(/\s*(?:\+|,|&|and)\s*/i) : [...(levels ?? [])];
  const codes = list.map((l) => l.trim().toUpperCase()).filter(Boolean);
  if (codes.length === 0 || !codes.every((c) => c in LEVEL_YEARS)) return null;
  const lo = Math.min(...codes.map((c) => LEVEL_YEARS[c]![0]));
  const highs = codes.map((c) => LEVEL_YEARS[c]![1]);
  const hi = highs.includes(null) ? null : Math.max(...(highs as number[]));
  return hi === null ? `${lo}+ yrs` : `${lo}–${hi} yrs`;
}

/** One way to write experience everywhere (#238): "5+ Years (Job Target)" / "5+ yrs" → "5+ years". */
export function formatExperience(s: string): string {
  return s
    .replace(/\s*\(job target\)/i, "")
    .replace(/\s*\b(?:yrs?|years?)\b\.?/i, " years")
    .trim();
}

export function getRequisitionExperience(req: {
  title?: string;
  config?: { experience?: string; targetExperience?: string; levels?: string[] };
}): string {
  return formatExperience(rawRequisitionExperience(req));
}

function rawRequisitionExperience(req: {
  title?: string;
  config?: { experience?: string; targetExperience?: string; levels?: string[] };
}): string {
  // What the recruiter typed on the requirement page wins, so the list and the page agree.
  const typed = req.config?.experience?.trim();
  if (typed) return levelsToYears(typed) ?? typed;
  if (req.config?.targetExperience) return levelsToYears(req.config.targetExperience) ?? req.config.targetExperience;
  const fromLevels = levelsToYears(req.config?.levels ?? []);
  if (fromLevels) return fromLevels;
  const title = (req.title || "").toLowerCase();
  if (title.includes("staff") || title.includes("lead") || title.includes("principal")) return "7+ yrs";
  if (title.includes("senior") || title.includes("5+")) return "5+ yrs";
  return "3+ yrs";
}

export function getRequisitionLocation(req: {
  region?: string;
  config?: { location?: string; workplaceMode?: string };
}): string {
  if (req.config?.location) {
    // New requests store the place alone and the mode separately; older ones baked "(Hybrid)" in.
    const loc = req.config.location.trim();
    const mode = req.config.workplaceMode?.trim();
    return mode && !/\((?:remote|hybrid|on-site)\)/i.test(loc) ? `${loc} (${mode})` : loc;
  }
  if (req.region === "in-central") return "Bangalore, India (Hybrid)";
  if (req.region === "us-east") return "New York, NY (Remote)";
  return "Remote / Hybrid";
}

const COMPETENCY_LABELS: Record<string, string> = {
  system_design: "System Design",
  coding_fundamentals: "Coding Fundamentals",
  debugging_operations: "Debugging & Ops",
  role_knowledge: "Role Expertise",
  problem_solving: "Problem Solving",
  communication: "Communication",
  collaboration: "Collaboration",
  ownership: "Ownership",
  customer_service: "Customer Experience",
  handover_clarity: "Handover Clarity",
  discovery_qualification: "Discovery & Qualification",
  objection_handling: "Objection Handling",
  pipeline_discipline: "Pipeline Discipline",
};

export function getRequisitionSkills(req: {
  title?: string;
  config?: { skills?: string[]; competencies?: string[] };
}): string[] {
  if (req.config?.skills && req.config.skills.length > 0) {
    return req.config.skills;
  }
  if (req.config?.competencies && req.config.competencies.length > 0) {
    return req.config.competencies.map((c) => COMPETENCY_LABELS[c] || c.replace(/_/g, " "));
  }
  const title = (req.title || "").toLowerCase();
  if (title.includes("java")) return ["Java", "Spring Boot", "Microservices", "PostgreSQL", "Kafka"];
  if (title.includes("react") || title.includes("frontend")) return ["React", "TypeScript", "Next.js", "Redux", "Tailwind CSS"];
  if (title.includes(".net")) return [".NET Core / C#", "ASP.NET WebAPI", "SQL Server", "Microservices", "Azure"];
  if (title.includes("devops") || title.includes("cloud")) return ["Kubernetes", "Terraform", "AWS / Azure", "Docker", "GitOps"];
  if (title.includes("nurse") || title.includes("icu")) return ["Critical Care", "Patient Triage", "Ventilator Protocols", "ACLS/BLS"];
  if (title.includes("support")) return ["Customer Service", "Zendesk", "SLA Resolution", "Incident Triage"];
  if (title.includes("sales")) return ["Enterprise Sales", "B2B Prospecting", "SaaS Negotiation", "Pipeline Management"];
  if (title.includes("hr")) return ["Talent Management", "Employee Relations", "Performance Systems", "HR Policies"];
  return ["Problem Solving", "Domain Knowledge", "Communication", "Execution"];
}

/** Who finds the candidates for a requirement: "We source" (Mode A — TalentHop's desk, marked by
 *  config.sourcing) or "I'll source" (the client brings them). Shown on the requirements list and
 *  the requirement header so the type is never only implied by the Sourcing progress card. */
export function getSourcingType(req: { config?: { sourcing?: boolean } | null }): {
  label: "We source" | "I'll source";
  cls: string;
  hint: string;
} {
  return req.config?.sourcing === true
    ? { label: "We source", cls: "pill t", hint: "TalentHop's team finds and screens the candidates for this job." }
    : { label: "I'll source", cls: "pill n", hint: "You bring the candidates: upload CVs, share the apply link or import them." };
}
